import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout.jsx';
import AdminAuthAPI from '../../api/admin/auth';
import './AdminTenantMetrics.css';

// Get correct API base URL
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  } else {
    return '/api';
  }
};

/**
 * Admin Tenant Metrics Detail Page
 * Detailed view of a single tenant's database metrics, sync history, and table health
 */
const AdminTenantMetrics = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showAllTables, setShowAllTables] = useState(false);

  useEffect(() => {
    loadTenantMetrics();
  }, [tenantId]);

  const loadTenantMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${getApiBaseUrl()}/admin/sync/monitoring/tenants/${tenantId}`, {
        headers: {
          'Authorization': `Bearer ${AdminAuthAPI.getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load tenant metrics: ${response.status}`);
      }

      const responseData = await response.json();
      setData(responseData);
    } catch (err) {
      console.error('Error loading tenant metrics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);

      const response = await fetch(`${getApiBaseUrl()}/admin/sync/monitoring/tenants/${tenantId}/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AdminAuthAPI.getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to refresh metrics: ${response.status}`);
      }

      const responseData = await response.json();
      setData(responseData);
    } catch (err) {
      console.error('Error refreshing metrics:', err);
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };

  const getStatusBadge = (status) => {
    const badges = {
      healthy: { class: 'badge-healthy', label: 'Healthy' },
      warning: { class: 'badge-warning', label: 'Warning' },
      error: { class: 'badge-error', label: 'Error' },
      empty: { class: 'badge-empty', label: 'Empty' },
      stale: { class: 'badge-stale', label: 'Stale' }
    };
    const badge = badges[status] || badges.healthy;
    return <span className={`status-badge ${badge.class}`}>{badge.label}</span>;
  };

  const getSyncStatusBadge = (status) => {
    const badges = {
      completed: { class: 'badge-completed', label: 'Completed' },
      running: { class: 'badge-running', label: 'Running' },
      failed: { class: 'badge-failed', label: 'Failed' },
      partial: { class: 'badge-partial', label: 'Partial' }
    };
    const badge = badges[status] || { class: 'badge-unknown', label: status };
    return <span className={`status-badge ${badge.class}`}>{badge.label}</span>;
  };

  if (loading && !data) {
    return (
      <AdminLayout>
        <div className="tenant-metrics-container">
          <div className="loading-state">Loading tenant metrics...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="tenant-metrics-container">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/admin/sync/monitoring">← Back to Monitoring Overview</Link>
        </div>

        {/* Tenant Header */}
        {data?.tenant && (
          <div className="tenant-header">
            <div className="header-content">
              <h1>{data.tenant.name}</h1>
              <div className="tenant-meta">
                <span className="meta-item">Schema: <strong>{data.tenant.schema_name}</strong></span>
                <span className="meta-item">Status: {getStatusBadge(data.tenant.status)}</span>
                <span className="meta-item">Created: {formatDateTime(data.tenant.created_at)}</span>
              </div>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="btn-refresh"
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        )}

        {error && (
          <div className="error-alert">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Sync Status Section */}
        {data?.sync_status && (
          <div className="section sync-status-section">
            <h2>Sync History</h2>
            {data.sync_status.recent_history && data.sync_status.recent_history.length > 0 ? (
              <div className="sync-history-list">
                {data.sync_status.recent_history.map((run) => (
                  <div key={run.id} className="sync-run-item">
                    <div className="sync-run-header">
                      <div className="sync-run-time">
                        {formatDateTime(run.started_at)}
                        {run.completed_at && ` - ${formatDateTime(run.completed_at)}`}
                      </div>
                      {getSyncStatusBadge(run.status)}
                    </div>
                    {run.error_message && (
                      <div className="sync-error-message">{run.error_message}</div>
                    )}
                    <div className="sync-run-stats">
                      <span>Processed: {formatNumber(run.records_processed || 0)}</span>
                      {run.records_failed > 0 && (
                        <span className="failed-count">Failed: {formatNumber(run.records_failed)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">No sync history available</div>
            )}

            {data.sync_status.recent_errors && data.sync_status.recent_errors.length > 0 && (
              <>
                <h3 className="subsection-title">Recent Errors (Last 7 Days)</h3>
                <div className="recent-errors-list">
                  {data.sync_status.recent_errors.slice(0, 10).map((error, idx) => (
                    <div key={idx} className="error-item">
                      <div className="error-header">
                        <span className="error-entity">{error.entity_type}</span>
                        <span className="error-time">{formatDateTime(error.created_at)}</span>
                      </div>
                      <div className="error-message">{error.error_message || 'Unknown error'}</div>
                      {error.genuine_id && (
                        <div className="error-id">Genuine ID: {error.genuine_id}</div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Critical Tables Section */}
        {data?.critical_tables && (
          <div className="section critical-tables-section">
            <h2>Critical Tables ({data.critical_tables.length})</h2>
            <div className="tables-grid">
              {data.critical_tables.map((table) => (
                <div key={table.table_name} className="table-card">
                  <div className="table-card-header">
                    <h3>{table.table_name}</h3>
                    {table.status && getStatusBadge(table.status)}
                  </div>
                  <div className="table-card-content">
                    <div className="metric">
                      <span className="metric-label">Records</span>
                      <span className="metric-value">{formatNumber(table.record_count)}</span>
                    </div>
                    {table.last_updated && (
                      <div className="metric">
                        <span className="metric-label">Last Updated</span>
                        <span className="metric-value small">{formatDateTime(table.last_updated)}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Tables Section */}
        {data?.all_tables && (
          <div className="section all-tables-section">
            <div className="section-header">
              <h2>All Tables ({data.all_tables.length})</h2>
              <button
                onClick={() => setShowAllTables(!showAllTables)}
                className="btn-toggle"
              >
                {showAllTables ? 'Hide' : 'Show'} All Tables
              </button>
            </div>
            {showAllTables && (
              <div className="all-tables-list">
                <table className="simple-table">
                  <thead>
                    <tr>
                      <th>Table Name</th>
                      <th>Record Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.all_tables.map((table) => (
                      <tr key={table.table_name}>
                        <td>{table.table_name}</td>
                        <td className="count">{formatNumber(table.record_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTenantMetrics;
